"use client";

import { ColumnDef } from "@tanstack/react-table";
import { User } from "@/context/user-context";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { SortableHeader } from "@/components/sortable_header";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Edit, Key, UserX, UserCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBranch } from "@/context/branch-context";
import { useIdentity } from "@/context/identity-context";
import { useRole } from "@/context/role-context";
import { useUpdateUser } from "./use-user-mutations";
import {
  ALL_BRANCHES_VALUE,
  fromBranchSelectValue,
  toBranchSelectValue,
} from "./user-utils";
import { useLocalization } from "@/context/localization-context";
import { translate } from "@/i18n/translate";
import { formatDate } from "@/lib/date-format";

/** Activating / deactivating a user, shared by the status switch and the row menu. */
const useUserStatus = (user: User) => {
  const { t } = useLocalization();
  const { identity } = useIdentity();
  const { updateUser, isUpdating } = useUpdateUser();

  return {
    isUpdating,
    // deactivating yourself would lock you out; the API refuses it too
    isSelf: identity?.payload?.user_id === user._id,
    toggle: () =>
      updateUser(
        user._id,
        { enabled: !user.enabled },
        {
          success: user.enabled
            ? t("users.status.deactivated")
            : t("users.status.activated"),
          error: t("users.status.updateFailed"),
        },
      ),
  };
};

const StatusToggle = ({ user }: { user: User }) => {
  const { t } = useLocalization();
  const { toggle, isUpdating, isSelf } = useUserStatus(user);

  return (
    <Switch
      checked={user.enabled}
      onCheckedChange={toggle}
      disabled={isUpdating || isSelf}
      title={isSelf ? t("users.status.cannotDeactivateSelf") : undefined}
      className="data-[state=checked]:bg-green-600"
    />
  );
};

const RoleSelect = ({ user }: { user: User }) => {
  const { t } = useLocalization();
  const { getRoleName, getAvailableRoles } = useRole();
  const { updateUser, isUpdating } = useUpdateUser();

  return (
    <Select
      value={user.role_id}
      onValueChange={(roleId) =>
        updateUser(
          user._id,
          { role_id: roleId },
          { success: t("users.role.updated"), error: t("users.role.updateFailed") },
        )
      }
      disabled={isUpdating}
    >
      <SelectTrigger className="w-[140px]">
        <SelectValue placeholder={t("users.fields.selectRole")}>
          {getRoleName(user.role_id)}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {getAvailableRoles().map((role) => (
          <SelectItem key={role._id} value={role._id}>
            {role.name || role.description}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const BranchSelect = ({ user }: { user: User }) => {
  const { t } = useLocalization();
  const { branches } = useBranch();
  const { updateUser, isUpdating } = useUpdateUser();

  const currentBranch = branches?.find(
    (branch) => branch.bb_branch_id === user.branch_id,
  );

  return (
    <Select
      value={toBranchSelectValue(user.branch_id)}
      onValueChange={(value) =>
        updateUser(
          user._id,
          { branch_id: fromBranchSelectValue(value) },
          {
            success: t("users.branch.updated"),
            error: t("users.branch.updateFailed"),
          },
        )
      }
      disabled={isUpdating}
    >
      <SelectTrigger className="w-[160px]">
        <SelectValue placeholder={t("users.fields.selectBranch")}>
          {user.branch_id === null
            ? t("users.fields.allBranches")
            : currentBranch?.name || user.branch_id}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_BRANCHES_VALUE}>{t("users.fields.allBranches")}</SelectItem>
        {branches
          ?.filter((branch) => branch.bb_branch_id)
          .map((branch) => (
            <SelectItem key={branch.bb_branch_id} value={branch.bb_branch_id}>
              {branch.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
};

const UserActions = ({
  user,
  onEdit,
  onChangePassword,
}: {
  user: User;
  onEdit: (user: User) => void;
  onChangePassword: (user: User) => void;
}) => {
  const { t } = useLocalization();
  const { toggle, isUpdating, isSelf } = useUserStatus(user);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">{t("users.actions.openMenu")}</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onEdit(user)}>
          <Edit className="mr-2 h-4 w-4" />
          {t("users.actions.edit")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onChangePassword(user)}>
          <Key className="mr-2 h-4 w-4" />
          {t("users.actions.changePassword")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={toggle} disabled={isUpdating || isSelf}>
          {user.enabled ? (
            <>
              <UserX className="mr-2 h-4 w-4" />
              {t("users.actions.deactivate")}
            </>
          ) : (
            <>
              <UserCheck className="mr-2 h-4 w-4" />
              {t("users.actions.activate")}
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Names shown in the column picker for the columns a user can hide (in the current language). */
export const getColumnLabels = (): Record<string, string> => ({
  login: translate("users.fields.login"),
  role_id: translate("users.fields.role"),
  branch_id: translate("users.fields.branchAccess"),
  enabled: translate("common.status"),
  updated_at: translate("users.fields.updatedAt"),
});

export const columns: (
  onEdit: (user: User) => void,
  onChangePassword: (user: User) => void,
) => ColumnDef<User>[] = (onEdit, onChangePassword) => [
  {
    accessorKey: "login",
    header: ({ column }) => <SortableHeader column={column} label={translate("users.fields.login")} />,
    cell: ({ row }) => {
      const user = row.original;
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
      return (
        <div className="flex flex-col">
          <div className="flex items-center space-x-2">
            <span className="font-medium">{user.login}</span>
            {!user.enabled && (
              <Badge variant="secondary" className="text-xs">
                {translate("users.status.inactive")}
              </Badge>
            )}
          </div>
          {fullName && (
            <span className="text-xs text-muted-foreground">{fullName}</span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "role_id",
    header: () => translate("users.fields.role"),
    cell: ({ row }) => <RoleSelect user={row.original} />,
    enableSorting: false,
  },
  {
    accessorKey: "branch_id",
    header: () => translate("users.fields.branchAccess"),
    cell: ({ row }) => <BranchSelect user={row.original} />,
    enableSorting: false,
  },
  {
    accessorKey: "enabled",
    header: () => translate("common.status"),
    cell: ({ row }) => {
      const user = row.original;
      return (
        <div className="flex items-center space-x-2">
          <StatusToggle user={user} />
          <span className="text-sm text-muted-foreground">
            {user.enabled
              ? translate("users.status.active")
              : translate("users.status.inactive")}
          </span>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "updated_at",
    header: ({ column }) => <SortableHeader column={column} label={translate("users.fields.updatedAt")} />,
    cell: ({ getValue }) => {
      const date = getValue() as string;
      if (!date) return "-";
      return formatDate(date);
    },
  },
  {
    id: "actions",
    header: () => translate("common.actions"),
    cell: ({ row }) => (
      <UserActions
        user={row.original}
        onEdit={onEdit}
        onChangePassword={onChangePassword}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
];
